import { Helmet } from "react-helmet";
import { useQuery } from '@apollo/client';
import { 
  GetUserLevelData, 
  GetAllLevelsData,
  LevelInfo
} from "../../types/userLevel";
import { 
  GET_USER_LEVEL, 
  GET_ALL_LEVELS
} from "../../graphql/queries/userLevels";
import { Loader } from "../../shared/Loader";

export const Levels = () => {
  // GraphQL queries using Apollo Client
  const {
    data: userLevelData,
    loading: userLevelLoading,
    error: userLevelError,
    refetch: refetchUserLevel,
  } = useQuery<GetUserLevelData>(GET_USER_LEVEL, {
    errorPolicy: 'all'
  });

  const {
    data: allLevelsData,
    loading: allLevelsLoading,
    error: allLevelsError,
  } = useQuery<GetAllLevelsData>(GET_ALL_LEVELS, {
    errorPolicy: 'all'
  });

  // Function to format currency
  const formatCurrency = (amount: number) => {
    const absAmount = Math.abs(amount);
    let value: string;

    if (absAmount >= 1e9) {
      value = (amount / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    } else if (absAmount >= 1e6) {
      value = (amount / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    } else if (absAmount >= 1e3) {
      value = (amount / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    } else {
      value = amount.toLocaleString();
    }

    return `₦${value}`;
  };

  // Get progress bar width
  const getProgressWidth = (progress?: number) => {
    if (!progress) return "0%";
    return `${Math.min(Math.max(progress, 0), 100)}%`;
  };

  // Get level gradient colors
  const getLevelGradient = (level: number, isUnlocked: boolean = true) => {
    if (!isUnlocked) {
      return "from-gray-800/80 to-gray-900/60";
    }
    const gradients = {
      1: "from-green-500/15 to-green-600/5",
      2: "from-blue-500/15 to-blue-600/5", 
      3: "from-yellow-500/15 to-yellow-600/5",
      4: "from-orange-500/15 to-orange-600/5",
      5: "from-red-500/15 to-red-600/5",
    };
    return gradients[level as keyof typeof gradients] || "from-gray-500/15 to-gray-600/5";
  };

  // Get level border colors
  const getLevelBorder = (level: number, isActive: boolean = false, isUnlocked: boolean = true) => {
    if (!isUnlocked) {
      return "border-gray-600/40";
    }
    const borders = {
      1: isActive ? "border-green-400/60" : "border-green-500/40",
      2: isActive ? "border-blue-400/60" : "border-blue-500/40",
      3: isActive ? "border-yellow-400/60" : "border-yellow-500/40", 
      4: isActive ? "border-orange-400/60" : "border-orange-500/40",
      5: isActive ? "border-red-400/60" : "border-red-500/40",
    };
    return borders[level as keyof typeof borders] || "border-gray-500/40";
  };

  const currentUserLevel = userLevelData?.getUserLevel;
  const allLevels = allLevelsData?.getAllLevels || [];

  const isLoading = userLevelLoading || allLevelsLoading;
  const hasError = userLevelError || allLevelsError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
      <Helmet>
        <title>My Level</title>
        <meta name="description" content="Your current trading level and progress"></meta>
        <link rel="canonical" href="https://gcbuying.com/app/levels" />
      </Helmet>
      
      <div className="container-lg px-4 py-8 lg:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-primary/70 flex items-center justify-center">
              <span className="text-2xl">🏆</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              My Level
            </h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Track your trading journey and unlock new achievements as you grow
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">
            {/* Content */}
            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <Loader />
                  <p className="mt-6 text-gray-400 text-lg">
                    Loading your level data...
                  </p>
                </div>
              </div>
            ) : hasError ? (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-6">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Failed to load level data
                </h3>
                <p className="text-red-400 mb-6">
                  {hasError.message}
                </p>
                <button
                  className="px-8 py-3 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 rounded-xl text-gray-900 font-semibold transition-all duration-300 transform hover:scale-105"
                  onClick={() => refetchUserLevel()}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Current Level Card */}
                {currentUserLevel && (
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-8">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-full transform translate-x-16 -translate-y-16"></div>
                    
                    <div className="relative">
                      <div className="flex items-start justify-between mb-8">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <div className="text-5xl">{currentUserLevel.badge}</div>
                            <div>
                              <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                                {currentUserLevel.currentLevelName}
                              </h2>
                              <p className="text-gray-400 text-lg">
                                {currentUserLevel.currentLevelDescription}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-400 mb-1">Level</div>
                          <div className="text-3xl font-bold text-primary">
                            {currentUserLevel.currentLevel}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 font-medium">Total Trade Value</span>
                          <span className="text-2xl font-bold text-white">
                            {formatCurrency(currentUserLevel.totalTradeValue)}
                          </span>
                        </div>
                        
                        {!currentUserLevel.isMaxLevel ? (
                          <div className="space-y-4">
                            {/* Progress Bar */}
                            <div className="relative">
                              <div className="w-full h-4 bg-gray-700/50 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                                  style={{ width: getProgressWidth(currentUserLevel.progressToNextLevel) }}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent animate-pulse"></div>
                                </div>
                              </div>
                              <div className="flex justify-between text-sm text-gray-400 mt-2">
                                <span>{formatCurrency(currentUserLevel.currentLevelThreshold)}</span>
                                <span className="font-semibold text-primary">
                                  {currentUserLevel.progressToNextLevel?.toFixed(1)}%
                                </span>
                                <span>{formatCurrency(currentUserLevel.nextLevelThreshold || 0)}</span>
                              </div>
                            </div>
                            
                            {/* Next Level Info */}
                            <div className="text-center bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-4">
                              <span className="text-primary font-semibold text-lg">
                                {formatCurrency(currentUserLevel.amountToNextLevel || 0)}
                              </span>
                              <span className="text-gray-300 ml-2">more to next level</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center bg-gradient-to-r from-primary/20 to-primary/10 rounded-xl p-6">
                            <span className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                              🎉 Maximum Level Achieved! 🎉
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* All Levels */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                    <span className="text-2xl">🎯</span>
                    All Levels
                  </h3>
                  
                  <div className="grid gap-4">
                    {allLevels.map((level: LevelInfo) => {
                      const isCurrentLevel = currentUserLevel?.currentLevel === level.level;
                      const isUnlocked = currentUserLevel && currentUserLevel.currentLevel >= level.level;
                      
                      return (
                        <div
                          key={level.level}
                          className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${getLevelGradient(level.level, isUnlocked)} backdrop-blur-sm border ${getLevelBorder(level.level, isCurrentLevel, isUnlocked)} p-6 transition-all duration-300 hover:scale-[1.02] ${
                            isCurrentLevel ? 'ring-2 ring-primary/30 shadow-lg shadow-primary/10' : ''
                          }`}
                          style={{
                            background: isUnlocked 
                              ? `linear-gradient(135deg, rgba(55, 65, 81, 0.8) 0%, rgba(31, 41, 55, 0.6) 100%)`
                              : `linear-gradient(135deg, rgba(55, 65, 81, 0.4) 0%, rgba(31, 41, 55, 0.3) 100%)`
                          }}
                        >
                          {/* Background decoration with better contrast */}
                          <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl ${
                            isUnlocked ? 'from-white/8 to-transparent' : 'from-white/3 to-transparent'
                          } rounded-full transform translate-x-12 -translate-y-12`}></div>
                          
                          <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`text-4xl ${!isUnlocked ? 'grayscale opacity-50' : ''} transition-all duration-300`}>
                                {level.badge}
                              </div>
                              <div>
                                <h4 className={`text-xl font-bold ${
                                  isUnlocked ? 'text-white' : 'text-gray-400'
                                }`}>
                                  Level {level.level}: {level.name}
                                </h4>
                                <p className={`text-sm mb-1 ${
                                  isUnlocked ? 'text-gray-200' : 'text-gray-400'
                                }`}>
                                  {level.description}
                                </p>
                                <p className={`text-xs font-mono ${
                                  isUnlocked ? 'text-gray-200' : 'text-gray-300'
                                }`}>
                                  Threshold: {formatCurrency(level.threshold)}
                                  {level.nextThreshold && ` - ${formatCurrency(level.nextThreshold)}`}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                              {isCurrentLevel && (
                                <div className="bg-gradient-to-r from-primary to-primary/80 text-gray-900 px-4 py-2 rounded-full text-sm font-bold">
                                  Current
                                </div>
                              )}
                              {isUnlocked && !isCurrentLevel && currentUserLevel && currentUserLevel.currentLevel > level.level && (
                                <div className="text-green-400 text-sm font-semibold flex items-center gap-1">
                                  <span>✓</span> Unlocked
                                </div>
                              )}
                              {!isUnlocked && (
                                <div className="text-gray-500 text-sm flex items-center gap-1">
                                  <span>🔒</span> Locked
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* About Section */}
            <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">💡</span>
                <h3 className="text-xl font-bold text-white">About User Levels</h3>
              </div>
              <div className="space-y-3 text-gray-300">
                <p className="text-sm leading-relaxed">
                  User levels are based on your total trade value across all transactions.
                </p>
                <p className="text-sm leading-relaxed">
                  <span className="text-primary font-semibold">Trade value</span> includes
                  gift card sales, crypto trades, wallet crypto sales, and quick crypto deposits.
                </p>
                <p className="text-sm leading-relaxed">
                  Higher levels may unlock exclusive features and better rates in the future.
                </p>
                <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <p className="text-xs text-primary font-medium">
                    💎 Levels are updated in real-time based on your completed transactions.
                  </p>
                </div>
              </div>
            </div>

            {/* Progress Summary */}
            {currentUserLevel && (
              <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">📊</span>
                  <h3 className="text-xl font-bold text-white">Progress Summary</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/30">
                    <span className="text-gray-400 text-sm font-medium">Current Level</span>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{currentUserLevel.badge}</span>
                      <span className="text-white font-bold text-lg">
                        {currentUserLevel.currentLevel}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-800/30">
                    <span className="text-gray-400 text-sm font-medium">Total Trades</span>
                    <span className="text-primary font-bold">
                      {formatCurrency(currentUserLevel.totalTradeValue)}
                    </span>
                  </div>
                  {!currentUserLevel.isMaxLevel && (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
                      <span className="text-gray-300 text-sm font-medium">Next Level</span>
                      <span className="text-primary font-bold">
                        {formatCurrency(currentUserLevel.amountToNextLevel || 0)} away
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Level Guide */}
            {allLevels.length > 0 && (
              <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">🗺️</span>
                  <h3 className="text-xl font-bold text-white">Level Guide</h3>
                </div>
                <div className="space-y-3">
                  {allLevels.slice(0, 3).map((level) => (
                    <div key={level.level} className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/30 hover:bg-gray-800/50 transition-colors">
                      <span className="text-2xl">{level.badge}</span>
                      <div>
                        <p className="text-white text-sm font-semibold">{level.name}</p>
                        <p className="text-primary text-xs font-mono">
                          {formatCurrency(level.threshold)}+
                        </p>
                      </div>
                    </div>
                  ))}
                  {allLevels.length > 3 && (
                    <div className="text-center pt-2">
                      <p className="text-gray-400 text-xs">
                        ...and {allLevels.length - 3} more levels to discover
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 